import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { WifiOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export function OfflineBanner() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [queued, setQueued] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const canRetry = ["admin", "analyste", "coordinateur"].includes(user.role);

  const load = useCallback(() => {
    api.get("/system/status").then((r) => setQueued(r.data.queued_pushes || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  if (queued <= 0) return null;

  const retry = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post("/dhis2/retry");
      toast.success(`${data.retried} ${t("replayed")}`);
      load();
    } catch {
      toast.error(t("kobo_test_fail"));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div data-testid="offline-banner" className="flex items-center justify-between gap-4 px-6 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-800 animate-fade-up">
      <div className="flex items-center gap-2 text-sm">
        <WifiOff className="w-4 h-4 shrink-0" />
        <span>{t("offline_banner").replace("{n}", queued)}</span>
      </div>
      {canRetry && (
        <button data-testid="offline-retry" onClick={retry} disabled={retrying}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white transition-colors disabled:opacity-60 shrink-0">
          <RefreshCw className={`w-3.5 h-3.5 ${retrying ? "animate-spin" : ""}`} /> {t("replay")}
        </button>
      )}
    </div>
  );
}
